const MAX_ID = 2147483647;
const MAX_QUANTITY = 1000000000000;
const SUPPORTED_UNITS = new Set(["EA","SF","SY","LF","BOX","ROLL","YD"]);
const text = (value) => String(value ?? "").trim();
const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const validId = (value) => /^\d+$/.test(text(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0 && Number(value) <= MAX_ID;
const unit = (value) => text(value).toUpperCase();
const structure = (value) => text(value).toLowerCase();

export const materialUnitLabel = (value) => ({
  SF:"square feet",
  SY:"square yards",
  EA:"each",
  LF:"linear feet",
  BOX:"boxes",
  ROLL:"rolls",
  YD:"yards",
}[unit(value)] || unit(value));

export const parseMonthlyMaterialQuantity = (value) => {
  const raw = text(value);
  if (!raw) return {valid:false,answered:false,value:null};
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw)) return {valid:false,answered:true,value:null};
  const decimals = (raw.split(".")[1] || "").replace(/0+$/, "");
  const number = Number(raw);
  const canonicalDecimal = (value) => {
    const [whole,fraction = ""] = value.split(".");
    const decimal = fraction.replace(/0+$/, "");
    return (whole.replace(/^0+/, "") || "0") + (decimal ? `.${decimal}` : "");
  };
  return Number.isFinite(number) && number >= 0 && number <= MAX_QUANTITY && decimals.length <= 4 &&
    canonicalDecimal(raw) === canonicalDecimal(String(number))
    ? {valid:true,answered:true,value:number} : {valid:false,answered:true,value:null};
};

export const normalizeMaterialOptions = (options) => {
  const candidates = (Array.isArray(options) ? options : []).filter((option) => record(option) &&
    validId(option.productId) && validId(option.storeId) && text(option.productName) && SUPPORTED_UNITS.has(unit(option.uom)))
    .map((option) => ({productId:Number(option.productId),productName:text(option.productName),
      productStructure:text(option.productStructure),uom:unit(option.uom),storeId:Number(option.storeId)}));
  const frequencies = new Map();
  candidates.forEach((option) => {
    const key = `${option.storeId}:${option.productId}`;
    frequencies.set(key,(frequencies.get(key) || 0) + 1);
  });
  // Ambiguous choices are unavailable, never resolved by picking the first one.
  return candidates.filter((option) => frequencies.get(`${option.storeId}:${option.productId}`) === 1)
    .sort((a,b) => a.storeId - b.storeId || a.productId - b.productId);
};

export const materialCatalogSignature = (options) => JSON.stringify(normalizeMaterialOptions(options));
export const materialOptionsForProperty = (property) => normalizeMaterialOptions(property?.materialOptions)
  .filter((option) => validId(property?.storeId) && option.storeId === Number(property.storeId))
  .sort((a,b) => a.productName.localeCompare(b.productName) || a.productId - b.productId);

// The API owns assignment and catalog qualification. This mirrors the manual
// candidate contract only to keep a malformed response from opening an editor
// that Sales can never complete. Zero-only source rows do not create demand.
export const hasCompleteManualMaterialChoices = (property) => {
  if (property?.isManualException !== true || !Array.isArray(property.layouts) || !property.layouts.length) return false;
  const options = materialOptionsForProperty(property);
  const sources = new Map();
  for (const layout of property.layouts) {
    let hasPositiveSource = false;
    if (!Array.isArray(layout?.materials)) return false;
    for (const material of layout.materials) {
      const parsed = parseMonthlyMaterialQuantity(material?.quantity);
      if (!parsed.valid) return false;
      if (parsed.value === 0) continue;
      const sourceStructure = structure(material?.productStructure);
      const sourceUnit = unit(material?.uom);
      if (!validId(material?.productId) || !sourceStructure || !SUPPORTED_UNITS.has(sourceUnit)) return false;
      hasPositiveSource = true;
      const productId = Number(material.productId);
      const existing = sources.get(productId);
      if (existing && (existing.productStructure !== sourceStructure || existing.uom !== sourceUnit)) return false;
      sources.set(productId,{productStructure:sourceStructure,uom:sourceUnit});
    }
    if (!hasPositiveSource) return false;
  }
  return sources.size > 0 && [...sources.values()].every((source) => options.some((option) =>
    structure(option.productStructure) === source.productStructure && option.uom === source.uom));
};

export const normalizeMonthlyMaterials = (materials) => materials == null ? null : materials.map((item) => ({
  productId:item.productId ?? null,quantity:item.quantity ?? null,uom:unit(item.uom),
}));
export const monthlyMaterialsToDraft = (materials) => materials == null ? null : materials.map((item) => ({
  productId:item.productId ?? "",quantity:text(item.quantity),uom:unit(item.uom),
  ...(validId(item.sourceProductId) ? {sourceProductId:Number(item.sourceProductId)} : {}),
}));
// Keep old-client signatures stable. Revision-aware drafts additionally compare
// source selections and submit them in the separate materialSelections field.
export const monthlyMaterialSignature = (materials) => materials == null ? "auto" : JSON.stringify(
  monthlyMaterialsToDraft(materials).map(({productId,quantity,uom}) => ({productId,quantity,uom}))
);

export const materialSelectionsForRows = (rows) => rows == null ? null : rows.map((row) => ({
  sourceProductId:Number(row.sourceProductId),productId:Number(row.productId),
})).sort((a,b) => a.sourceProductId - b.sourceProductId);

export const savedMaterialsToDraft = (property) => {
  if (property.materialForecasts == null) return null;
  if (!Array.isArray(property.materialSelections)) return monthlyMaterialsToDraft(property.materialForecasts);
  return property.materialSelections.map((selection) => {
    const saved = property.savedSnapshot?.materials?.find((row) => Number(row.sourceProductId) === Number(selection.sourceProductId));
    const target = property.materialForecasts.find((row) => Number(row.productId) === Number(selection.productId));
    return {sourceProductId:selection.sourceProductId,productId:selection.productId,
      quantity:text(saved?.quantity ?? target?.quantity),uom:unit(saved?.uom || target?.uom)};
  });
};

const calculatedMaterials = (property, counts) => {
  if (!counts?.complete) return {rows:[],estimateError:"Enter valid unit counts to see the calculated material estimate."};
  const options = materialOptionsForProperty(property);
  const rows = new Map();
  let estimateError = "";
  (property.layouts || []).forEach((layout) => {
    const count = counts.layouts.find((item) => Number(item.unitTypeId) === Number(layout.unitTypeId))?.forecastUnits;
    const perApartment = new Map();
    (layout.materials || []).forEach((material) => {
      const quantity = Number(material.quantity);
      if (property?.isManualException === true && material.quantity != null && Number.isFinite(quantity) && quantity === 0) return;
      if (!validId(material.productId) || material.quantity == null || !Number.isFinite(quantity) || quantity < 0 || !unit(material.uom)) {
        estimateError = "The material estimate is not available here. NANCY will calculate it from the unit counts.";
        return;
      }
      const productId = Number(material.productId);
      const option = options.find((item) => item.productId === productId);
      const existing = perApartment.get(productId) || rows.get(productId);
      if (existing && (existing.uom !== unit(material.uom) ||
          (property?.isManualException === true && structure(existing.productStructure) !== structure(material.productStructure)))) {
        estimateError = "The material units need checking before an estimate can be shown.";
        return;
      }
      const productName = material.privateName || option?.productName ||
        [material.manufacturer,material.style,material.color].filter(Boolean).join(" ") || `Material ${productId}`;
      perApartment.set(productId,{productId,sourceProductId:productId,productName,
        productStructure:text(material.productStructure),uom:unit(material.uom),quantity:(perApartment.get(productId)?.quantity || 0) + quantity});
    });
    perApartment.forEach((material,productId) => {
      const monthly = Number((Number(material.quantity.toFixed(4)) * (count || 0)).toFixed(4));
      rows.set(productId,{...material,quantity:(rows.get(productId)?.quantity || 0) + monthly});
    });
  });
  if ([...rows.values()].some((row) => !Number.isFinite(row.quantity))) estimateError = "The calculated material quantity is too large. Check the unit counts.";
  return {rows:estimateError ? [] : [...rows.values()].map((row) => ({...row,quantity:Number(row.quantity.toFixed(4))})),estimateError};
};

const calculatedSourcesForSelections = (materials, calculatedRows, strict = false) => {
  const matches = Array(materials.length).fill(null);
  const used = new Set();
  const assign = (index,candidate) => {
    if (!candidate || used.has(candidate.productId) || candidate.uom !== unit(materials[index].uom)) return false;
    matches[index] = candidate;
    used.add(candidate.productId);
    return true;
  };

  // A replacement chosen in this browser keeps the exact source identity.
  materials.forEach((item,index) => {
    if (!validId(item.sourceProductId)) return;
    assign(index,calculatedRows.find((row) => Number(row.productId) === Number(item.sourceProductId)));
  });
  if (strict) return {matches,complete:matches.every(Boolean) && used.size === calculatedRows.length && materials.length === calculatedRows.length};
  // A saved row that still uses the property's standard product is exact too.
  materials.forEach((item,index) => {
    if (matches[index]) return;
    assign(index,calculatedRows.find((row) => Number(row.productId) === Number(item.productId)));
  });

  // Older saved replacements did not retain their source. Infer only a
  // one-to-one unit match after exact rows are removed; array position is never
  // a safe identity because saved rows are sorted by product ID.
  let changed = true;
  while (changed) {
    changed = false;
    materials.forEach((item,index) => {
      if (matches[index]) return;
      const candidates = calculatedRows.filter((row) => !used.has(row.productId) && row.uom === unit(item.uom));
      const pendingForUnit = materials.filter((other,otherIndex) => !matches[otherIndex] && unit(other.uom) === unit(item.uom));
      if (candidates.length === 1 && pendingForUnit.length === 1 && assign(index,candidates[0])) changed = true;
    });
  }

  return {
    matches,
    complete: matches.every(Boolean) && used.size === calculatedRows.length && materials.length === calculatedRows.length,
  };
};

// Saved forecasts contain totals per selected RSO product, not browser source
// IDs. One selected product for a category/unit group can safely cover every
// source in that group. Multiple selected products in the same group do not
// reveal the original split, so leave that case invalid for explicit reselection.
const restoreManualSelections = (materials, calculatedRows, options) => {
  if (!materials.length || materials.some((item) => validId(item.sourceProductId))) return materials;
  const keyFor = (item) => `${structure(item.productStructure)}:${unit(item.uom)}`;
  const targets = new Map();
  for (const item of materials) {
    const option = options.find((choice) => Number(choice.productId) === Number(item.productId));
    if (!option || !structure(option.productStructure) || option.uom !== unit(item.uom)) return materials;
    const key = keyFor(option);
    if (targets.has(key)) return materials;
    targets.set(key,item);
  }
  const sourceKeys = new Set(calculatedRows.map(keyFor));
  if (!calculatedRows.length || sourceKeys.size !== targets.size ||
      [...sourceKeys].some((key) => !targets.has(key))) return materials;
  return calculatedRows.map((source) => ({...targets.get(keyFor(source)),sourceProductId:source.productId}));
};

export const getMonthlyMaterialState = (property, materials, counts) => {
  const options = materialOptionsForProperty(property);
  if (materials == null) return {custom:false,valid:true,errorMessage:"",materialForecasts:null,options,...calculatedMaterials(property,counts)};
  const calculated = calculatedMaterials(property,counts);
  const strict = Number.isInteger(property?.forecastRevision);
  if (strict && materials === property.materialForecasts) materials = savedMaterialsToDraft(property);
  if (!strict && property?.isManualException === true) materials = restoreManualSelections(materials,calculated.rows,options);
  // An older save may know the selected product and total without knowing the
  // original source. Keep those saved facts visible, but never recalculate or
  // submit them as a source mapping. Sales must explicitly choose each source.
  const needsSourceSelections = strict && property.forecastRevision > 0 && property.materialSelections == null &&
    Array.isArray(property.materialForecasts) && materials.length > 0 && materials.every((item) => !validId(item.sourceProductId)) &&
    monthlyMaterialSignature(materials) === monthlyMaterialSignature(property.materialForecasts);
  const sources = calculatedSourcesForSelections(materials,calculated.rows,strict);
  const ids = materials.map((item) => validId(item.productId) ? Number(item.productId) : null);
  const rows = materials.map((item,index) => {
    const option = options.find((choice) => choice.productId === ids[index]);
    const source = sources.matches[index];
    const quantity = source ? text(source.quantity) : text(item.quantity);
    const parsed = parseMonthlyMaterialQuantity(quantity);
    const productValid = !!option && unit(item.uom) === option.uom &&
      (property?.isManualException !== true || (!!source && structure(source.productStructure) === structure(option.productStructure)));
    const duplicate = !strict && property?.isManualException !== true && ids[index] !== null && ids.filter((id) => id === ids[index]).length > 1;
    const savedNames = [...new Set((property.savedSnapshot?.materials || []).filter((row) => Number(row.productId) === ids[index])
      .map((row) => text(row.productName)).filter(Boolean))];
    return {...item,quantity,uom:unit(item.uom),...(source ? {sourceProductId:source.productId} : {}),
      sourceProductName:source?.productName || "",sourceProductStructure:source?.productStructure || "",
      productName:needsSourceSelections ? savedNames.length === 1 ? savedNames[0] : "Saved material" : option?.productName || "Choose an available RSO material",
      parsed,productValid,duplicate,sourceValid:!!source,invalid:!productValid || duplicate || !parsed.valid || !source};
  });
  let errorMessage = "";
  if (!rows.length) errorMessage = "This material selection cannot be displayed. Select Keep current material before saving.";
  else if (rows.length > 1000) errorMessage = "This material selection has more than 1,000 rows. Select Keep current material before saving.";
  else if (rows.some((row) => !row.productValid)) errorMessage = "Choose another RSO material, or select Keep current material.";
  else if (rows.some((row) => row.duplicate)) errorMessage = "This material selection repeats a product. Select Keep current material before saving.";
  else if (rows.some((row) => !row.parsed.valid)) errorMessage = "A saved material amount is invalid. Select Keep current material before saving.";
  else if (calculated.estimateError) errorMessage = calculated.estimateError;
  else if (!sources.complete) errorMessage = "We cannot work out the amount for this saved material. Select Keep current material before saving changes.";
  const grouped = new Map();
  if (!errorMessage) rows.forEach((row) => {
    const existing = grouped.get(Number(row.productId));
    const quantity = Number(((existing?.quantity || 0) + row.parsed.value).toFixed(4));
    grouped.set(Number(row.productId),{productId:Number(row.productId),quantity,uom:row.uom});
  });
  if (!errorMessage && [...grouped.values()].some((row) => !parseMonthlyMaterialQuantity(row.quantity).valid)) {
    errorMessage = "The calculated material quantity is too large. Check the unit counts.";
  }
  const canRemap = needsSourceSelections && calculated.rows.length > 0 && calculated.rows.every((row) => options.some((option) =>
    option.uom === row.uom && (property.isManualException !== true || structure(option.productStructure) === structure(row.productStructure))));
  return {custom:true,valid:!errorMessage,errorMessage,options,rows,estimateError:"",needsSourceSelections,canRemap,
    materialForecasts:errorMessage ? null : [...grouped.values()].sort((a,b) => a.productId - b.productId)};
};

export const seedMonthlyMaterials = (property, counts) => {
  const {rows} = calculatedMaterials(property,counts);
  return rows.length ? monthlyMaterialsToDraft(rows) : [{productId:"",quantity:"",uom:""}];
};

const validReceiptMaterials = (materials) => materials == null || (Array.isArray(materials) && materials.length > 0 && materials.length <= 1000 &&
  materials.every((item) => record(item) && validId(item.productId) && typeof item.quantity === "number" &&
    parseMonthlyMaterialQuantity(item.quantity).valid && SUPPORTED_UNITS.has(unit(item.uom))) &&
  new Set(materials.map((item) => Number(item.productId))).size === materials.length);

export const monthlyMaterialReceiptMatches = (expected, received) => {
  if (!validReceiptMaterials(expected) || !validReceiptMaterials(received)) return false;
  if (expected == null || received == null) return expected == null && received == null;
  if (expected.length !== received.length) return false;
  return expected.every((item) => received.some((other) => Number(other.productId) === Number(item.productId) &&
    other.quantity === item.quantity && unit(other.uom) === unit(item.uom)));
};
